import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  BookOpen, Download, Languages, UserSearch, Loader2, FileText,
  ChevronRight, Globe, Trash2, CheckCircle, XCircle, BarChart3,
  Users, ArrowRight, Printer, Mic, BookText, PenLine, Play,
  RotateCcw, TrendingUp, Sparkles, Volume2, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('mosaic_session_token');
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const GRADE_BANDS = [
  { value: "K", label: "Kindergarten" }, { value: "1", label: "Grade 1" },
  { value: "2", label: "Grade 2" }, { value: "3-4", label: "Grades 3-4" },
  { value: "5-6", label: "Grades 5-6" }, { value: "7-8", label: "Grades 7-8" },
  { value: "9-12", label: "Grades 9-12" },
];
const MODALITIES = [
  { value: "", label: "All Modalities" }, { value: "listening", label: "Listening" },
  { value: "reading", label: "Reading" }, { value: "speaking", label: "Speaking" },
  { value: "writing", label: "Writing" },
];
const WIDA_LEVELS = [
  { value: 1, label: "L1", color: "bg-red-500" }, { value: 2, label: "L2", color: "bg-orange-500" },
  { value: 3, label: "L3", color: "bg-yellow-500" }, { value: 4, label: "L4", color: "bg-blue-500" },
  { value: 5, label: "L5", color: "bg-indigo-500" }, { value: 6, label: "L6", color: "bg-green-500" },
];

const TOOL_CARDS = [
  { id: 'workbook', icon: BookOpen, title: 'Practice Workbooks', desc: 'Print-ready student workbooks from the question bank', color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { id: 'parent-letter', icon: Languages, title: 'Parent Letters', desc: 'Multi-language MOSAIC proficiency progress letters for families', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { id: 'screener', icon: UserSearch, title: 'ELL Screener', desc: 'Quick placement assessment for new students', color: 'text-green-600 bg-green-50 border-green-200' },
  { id: 'speaking-lab', icon: Mic, title: 'Speaking Practice Lab', desc: 'automated MOSAIC proficiency feedback on student speaking responses', color: 'text-sky-600 bg-sky-50 border-sky-200' },
  { id: 'vocab-builder', icon: BookText, title: 'Vocabulary Builder', desc: 'Interactive proficiency-level vocabulary practice with scoring', color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { id: 'writing-portfolio', icon: PenLine, title: 'Writing Portfolio', desc: 'automated writing with MOSAIC proficiency feedback and growth tracking', color: 'text-rose-600 bg-rose-50 border-rose-200' },
];

const TeacherToolkitTab = ({ classes = [], students = [] }) => {
  const [activeTool, setActiveTool] = useState(null);
  const [license, setLicense] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/licenses/check/my-school`, authHeader()).then(res => {
      setLicense(res.data);
    }).catch(() => {
      setLicense({ toolkit_access: false });
    }).finally(() => setLicenseLoading(false));
  }, []);

  const hasToolkitAccess = license?.toolkit_access || license?.is_super_admin;

  if (licenseLoading) {
    return <div className="py-12 text-center"><Loader2 className="animate-spin mx-auto text-[#002FA7]" size={24} /></div>;
  }

  if (!hasToolkitAccess) {
    return (
      <div className="space-y-5" data-testid="teacher-toolkit-locked">
        <div>
          <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Teacher Toolkit</h2>
          <p className="text-xs text-gray-500">Professional automated tools for ENL instruction</p>
        </div>
        <div className="bg-white border-2 border-gray-200 rounded-lg p-8 text-center">
          <Lock size={40} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">Teacher Toolkit — Premium Feature</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">
            The Teacher Toolkit includes 6 automated tools: Speaking Practice Lab, Vocabulary Builder, Writing Portfolio, 
            Practice Workbooks, Parent Letters, and ELL Screener.
          </p>
          <p className="text-xs text-gray-400 mb-6">Contact your school administrator to add the Teacher Toolkit to your school's MOSAIC license.</p>
          <div className="grid md:grid-cols-3 gap-3 max-w-2xl mx-auto opacity-50">
            {TOOL_CARDS.slice(0, 6).map(tool => (
              <div key={tool.id} className={`text-left bg-white border rounded-lg p-4 ${tool.color.split(' ').slice(1).join(' ')}`}>
                <tool.icon size={20} className={`${tool.color.split(' ')[0]} mb-2`} />
                <h4 className="font-bold text-gray-700 text-xs">{tool.title}</h4>
                <p className="text-[10px] text-gray-400 mt-0.5">{tool.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 mt-6">
            Pricing: $299/grade/building/year (add-on) or $499/building/year (standalone)
          </p>
        </div>
      </div>
    );
  }

  if (!activeTool) {
    return (
      <div className="space-y-5" data-testid="teacher-toolkit-home">
        <div>
          <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Teacher Toolkit</h2>
          <p className="text-xs text-gray-500">Professional tools for ENL instruction, parent communication, and student placement</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {TOOL_CARDS.map(tool => (
            <button key={tool.id} onClick={() => setActiveTool(tool.id)} className={`text-left bg-white border rounded-lg p-5 hover:shadow-md transition-all group ${tool.color.split(' ').slice(1).join(' ')}`} data-testid={`tool-${tool.id}`}>
              <tool.icon size={28} className={`${tool.color.split(' ')[0]} mb-3`} />
              <h3 className="font-bold text-gray-800 text-sm mb-1">{tool.title}</h3>
              <p className="text-xs text-gray-500">{tool.desc}</p>
              <div className="flex items-center gap-1 mt-3 text-xs font-bold text-gray-400 group-hover:text-gray-600 transition-colors">
                Open <ChevronRight size={12} />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`toolkit-${activeTool}`}>
      <div className="flex items-center gap-2">
        <Button onClick={() => setActiveTool(null)} variant="outline" size="sm">Back to Toolkit</Button>
      </div>
      {activeTool === 'workbook' && <WorkbookTool />}
      {activeTool === 'parent-letter' && <ParentLetterTool students={students} />}
      {activeTool === 'screener' && <ScreenerTool />}
      {activeTool === 'speaking-lab' && <SpeakingLabTool />}
      {activeTool === 'vocab-builder' && <VocabBuilderTool />}
      {activeTool === 'writing-portfolio' && <WritingPortfolioTool />}
    </div>
  );
};

// ════════════════════════════════════════════════════════════
// WORKBOOK TOOL
// ════════════════════════════════════════════════════════════
const WorkbookTool = () => {
  const [form, setForm] = useState({ grade_level: '', modality: '', wida_levels: [], max_questions: 40, include_answer_key: true, isbn: '' });
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);

  const toggleLevel = (lvl) => {
    setForm(prev => {
      const levels = prev.wida_levels.includes(lvl) ? prev.wida_levels.filter(l => l !== lvl) : [...prev.wida_levels, lvl].sort();
      return { ...prev, wida_levels: levels };
    });
  };

  const fetchPreview = async () => {
    if (!form.grade_level) return;
    try {
      const wl = form.wida_levels.join(',');
      const res = await axios.get(`${API}/teacher-toolkit/workbook/preview?grade_level=${form.grade_level}&modality=${form.modality}&wida_levels=${wl}`, authHeader());
      setPreview(res.data);
    } catch { setPreview(null); }
  };

  useEffect(() => { if (form.grade_level) fetchPreview(); }, [form.grade_level, form.modality, form.wida_levels.join(',')]);

  const handleGenerate = async () => {
    if (!form.grade_level) { toast.error('Select a grade band'); return; }
    setGenerating(true);
    toast.info('Generating print-ready workbook...');
    try {
      const res = await axios.post(`${API}/teacher-toolkit/workbook/generate`, form, { responseType: 'blob', ...authHeader() });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `MOSAIC_Practice_Workbook_${form.grade_level}_${form.modality || 'all'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Workbook downloaded!');
    } catch (err) {
      toast.error('Failed to generate workbook');
    } finally { setGenerating(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Student Practice Workbooks</h2>
        <p className="text-xs text-gray-500">Generate print-ready PDF workbooks from the MOSAIC question bank. Includes cover page, table of contents, answer bubbles, writing lines, and teacher answer key.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Grade Band *</label>
            <select value={form.grade_level} onChange={e => setForm(p => ({ ...p, grade_level: e.target.value }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="wb-grade">
              <option value="">Select...</option>
              {GRADE_BANDS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Modality</label>
            <select value={form.modality} onChange={e => setForm(p => ({ ...p, modality: e.target.value }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="wb-modality">
              {MODALITIES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Proficiency Levels (leave empty for all)</label>
          <div className="flex flex-wrap gap-2">
            {WIDA_LEVELS.map(lvl => {
              const sel = form.wida_levels.includes(lvl.value);
              return (
                <button key={lvl.value} onClick={() => toggleLevel(lvl.value)} className={`px-3 py-1.5 text-xs font-bold rounded-full border-2 transition-all ${sel ? `${lvl.color} text-white border-transparent` : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`} data-testid={`wb-level-${lvl.value}`}>
                  {lvl.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Max Questions</label>
            <select value={form.max_questions} onChange={e => setForm(p => ({ ...p, max_questions: parseInt(e.target.value) }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="wb-max">
              {[10, 20, 30, 40, 50, 60, 80].map(n => <option key={n} value={n}>{n} questions</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.include_answer_key} onChange={e => setForm(p => ({ ...p, include_answer_key: e.target.checked }))} className="rounded" data-testid="wb-answer-key" />
              <span className="text-sm text-gray-700">Include Answer Key (Teacher Edition)</span>
            </label>
          </div>
        </div>

        {/* ISBN (optional) */}
        <div>
          <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">ISBN (optional — for commercial printing)</label>
          <Input value={form.isbn} onChange={e => setForm(p => ({ ...p, isbn: e.target.value }))} placeholder="e.g., 978-0-1234-5678-9" className="h-10 border-gray-300 max-w-xs" data-testid="wb-isbn" />
          <p className="text-[10px] text-gray-400 mt-0.5">Adds barcode to back cover. Leave blank if not needed.</p>
        </div>

        {/* Preview */}
        {preview && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-bold text-blue-700 mb-1">Available: {preview.total_questions} questions</p>
            <div className="flex gap-3 flex-wrap">
              {Object.entries(preview.by_modality).map(([mod, count]) => (
                <span key={mod} className="text-[10px] bg-white px-2 py-0.5 rounded text-blue-600 border border-blue-200 capitalize">{mod}: {count}</span>
              ))}
            </div>
          </div>
        )}

        <Button onClick={handleGenerate} disabled={generating} className="w-full h-12 bg-[#002FA7] hover:bg-[#002FA7]/90 text-white text-base font-bold" data-testid="wb-generate-btn">
          {generating ? <><Loader2 size={18} className="animate-spin mr-2" /> Generating Print-Ready PDF...</> : <><Download size={18} className="mr-2" /> Generate & Download Workbook</>}
        </Button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════
// PARENT LETTER TOOL
// ════════════════════════════════════════════════════════════
const ParentLetterTool = ({ students = [] }) => {
  const [languages, setLanguages] = useState([]);
  const [form, setForm] = useState({ student_id: '', language: 'en', school_name: '', teacher_name: '' });
  const [generating, setGenerating] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    axios.get(`${API}/teacher-toolkit/parent-letter/languages`, authHeader()).then(r => setLanguages(r.data.languages || [])).catch(() => {});
  }, []);

  const searchStudents = async (q) => {
    setStudentSearch(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/students?search=${encodeURIComponent(q)}&limit=10`, authHeader());
      setSearchResults(res.data.students || []);
    } catch { setSearchResults([]); }
  };

  const selectStudent = (s) => {
    setForm(prev => ({ ...prev, student_id: s.user_id }));
    setStudentSearch(s.name);
    setSearchResults([]);
  };

  const handleGenerate = async () => {
    if (!form.student_id) { toast.error('Select a student'); return; }
    setGenerating(true);
    const langName = languages.find(l => l.code === form.language)?.name || form.language;
    toast.info(`Generating letter in ${langName}...`);
    try {
      const res = await axios.post(`${API}/teacher-toolkit/parent-letter/generate`, form, { responseType: 'blob', ...authHeader() });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `MOSAIC_Parent_Letter_${langName}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Letter downloaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate letter');
    } finally { setGenerating(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Parent Progress Letters</h2>
        <p className="text-xs text-gray-500">Generate personalized MOSAIC proficiency progress letters in 10 languages. Includes proficiency level, modality scores, and home activity suggestions.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        {/* Student search */}
        <div className="relative">
          <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Student *</label>
          <Input value={studentSearch} onChange={e => searchStudents(e.target.value)} placeholder="Type student name to search..." className="h-10 border-gray-300" data-testid="pl-student-search" />
          {searchResults.length > 0 && (
            <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-md mt-1 shadow-lg max-h-40 overflow-y-auto">
              {searchResults.map(s => (
                <button key={s.user_id} onClick={() => selectStudent(s)} className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm" data-testid={`pl-student-${s.user_id}`}>
                  {s.name} <span className="text-xs text-gray-400 ml-1">{s.email}</span>
                </button>
              ))}
            </div>
          )}
          {form.student_id && <p className="text-[10px] text-green-600 mt-0.5">Student selected</p>}
        </div>

        {/* Language */}
        <div>
          <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Language</label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {languages.map(lang => (
              <button key={lang.code} onClick={() => setForm(p => ({ ...p, language: lang.code }))} className={`px-3 py-2 text-xs font-bold rounded-lg border-2 transition-all ${form.language === lang.code ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`} data-testid={`pl-lang-${lang.code}`}>
                {lang.name}
              </button>
            ))}
          </div>
        </div>

        {/* Optional fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">School Name (optional)</label>
            <Input value={form.school_name} onChange={e => setForm(p => ({ ...p, school_name: e.target.value }))} placeholder="Auto-detected from student record" className="h-10 border-gray-300" data-testid="pl-school" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Teacher Name (optional)</label>
            <Input value={form.teacher_name} onChange={e => setForm(p => ({ ...p, teacher_name: e.target.value }))} placeholder="Your name (auto-filled)" className="h-10 border-gray-300" data-testid="pl-teacher" />
          </div>
        </div>

        <Button onClick={handleGenerate} disabled={generating} className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white text-base font-bold" data-testid="pl-generate-btn">
          {generating ? <><Loader2 size={18} className="animate-spin mr-2" /> Generating & Translating...</> : <><Globe size={18} className="mr-2" /> Generate Parent Letter</>}
        </Button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════
// ELL SCREENER TOOL
// ════════════════════════════════════════════════════════════
const ScreenerTool = () => {
  const [view, setView] = useState('start'); // 'start' | 'active' | 'results' | 'history'
  const [form, setForm] = useState({ student_name: '', grade_level: '', home_language: '' });
  const [loading, setLoading] = useState(false);
  const [screener, setScreener] = useState(null);
  const [answers, setAnswers] = useState({});
  const [teacherObs, setTeacherObs] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/teacher-toolkit/screener/history`, authHeader());
      setHistory(res.data.screeners || []);
    } catch { }
    finally { setLoadingHistory(false); }
  };

  const startScreener = async () => {
    if (!form.student_name.trim() || !form.grade_level) { toast.error('Student name and grade required'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/teacher-toolkit/screener/start`, form, authHeader());
      setScreener(res.data);
      setCurrentQ(0);
      setAnswers({});
      setTeacherObs({});
      setView('active');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start screener'); }
    finally { setLoading(false); }
  };

  const submitScreener = async () => {
    if (!screener) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/teacher-toolkit/screener/${screener.screener_id}/submit`, {
        answers, teacher_observations: teacherObs
      }, authHeader());
      setResult(res.data);
      setView('results');
    } catch (err) { toast.error('Failed to submit'); }
    finally { setLoading(false); }
  };

  const deleteScreener = async (id) => {
    if (!window.confirm('Delete this screener record?')) return;
    try {
      await axios.delete(`${API}/teacher-toolkit/screener/${id}`, authHeader());
      toast.success('Deleted');
      setHistory(prev => prev.filter(s => s.screener_id !== id));
    } catch { toast.error('Failed to delete'); }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  // ── START VIEW ──
  if (view === 'start') {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>ELL Screener</h2>
            <p className="text-xs text-gray-500">Quick placement assessment for new ELL students. Estimates proficiency level across all 4 modalities.</p>
          </div>
          <Button onClick={() => { setView('history'); fetchHistory(); }} variant="outline" size="sm" className="gap-1" data-testid="scr-history-btn">
            <FileText size={14} /> History
          </Button>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Student Name *</label>
            <Input value={form.student_name} onChange={e => setForm(p => ({ ...p, student_name: e.target.value }))} placeholder="New student's full name" className="h-10 border-gray-300" data-testid="scr-name" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Grade Level *</label>
              <select value={form.grade_level} onChange={e => setForm(p => ({ ...p, grade_level: e.target.value }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="scr-grade">
                <option value="">Select...</option>
                {GRADE_BANDS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Home Language</label>
              <Input value={form.home_language} onChange={e => setForm(p => ({ ...p, home_language: e.target.value }))} placeholder="e.g., Spanish, Mandarin, Arabic" className="h-10 border-gray-300" data-testid="scr-language" />
            </div>
          </div>
          <Button onClick={startScreener} disabled={loading} className="w-full h-12 bg-green-600 hover:bg-green-700 text-white text-base font-bold" data-testid="scr-start-btn">
            {loading ? <><Loader2 size={18} className="animate-spin mr-2" /> Loading...</> : <><UserSearch size={18} className="mr-2" /> Start ELL Screener</>}
          </Button>
        </div>
      </div>
    );
  }

  // ── ACTIVE SCREENER ──
  if (view === 'active' && screener) {
    const questions = screener.questions || [];
    const q = questions[currentQ];
    if (!q) return null;
    const isLast = currentQ === questions.length - 1;
    const isTeacherObserved = q.teacher_observed;
    const progressPct = ((currentQ + 1) / questions.length * 100).toFixed(0);

    return (
      <div className="space-y-4" data-testid="scr-active">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-gray-700">Screening: {screener.student_name}</span>
          <span className="text-xs text-gray-500">Question {currentQ + 1} of {questions.length}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-bold text-white px-2 py-0.5 rounded capitalize ${q.modality === 'listening' ? 'bg-sky-500' : q.modality === 'reading' ? 'bg-green-500' : q.modality === 'speaking' ? 'bg-amber-500' : 'bg-purple-500'}`}>{q.modality}</span>
            <span className="text-[10px] text-gray-400">Proficiency Level {q.wida_level} | {q.difficulty_band}</span>
          </div>

          {q.bp_image_url && <img src={q.bp_image_url.startsWith('http') ? q.bp_image_url : `${process.env.REACT_APP_BACKEND_URL}${q.bp_image_url}`} alt="Support" className="max-h-40 rounded-lg border" />}
          {q.graphic_support_url && <img src={q.graphic_support_url} alt="Graphic" className="max-h-32 rounded-lg border" />}

          {q.stimulus && q.stimulus.length > 15 && <p className="text-sm text-gray-600 italic bg-gray-50 p-3 rounded">{q.stimulus}</p>}
          <p className="text-base text-gray-800 font-medium">{q.question_text}</p>

          {/* MC Options */}
          {q.options && q.options.length > 0 && (
            <div className="space-y-2">
              {q.options.map((opt, i) => {
                const labels = ['A', 'B', 'C', 'D', 'E'];
                const selected = answers[q.id] === opt;
                return (
                  <button key={i} onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))} className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all text-sm ${selected ? 'border-green-500 bg-green-50 font-bold' : 'border-gray-200 hover:border-gray-400'}`} data-testid={`scr-option-${i}`}>
                    <span className="font-bold text-gray-500 mr-2">{labels[i]}.</span> {opt}
                  </button>
                );
              })}
            </div>
          )}

          {/* Teacher observation for speaking/writing */}
          {isTeacherObserved && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
              <p className="text-xs font-bold text-amber-700">Teacher Observation — Rate student's response (1-6):</p>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5, 6].map(lvl => (
                  <button key={lvl} onClick={() => setTeacherObs(prev => ({ ...prev, [q.id]: lvl }))} className={`w-10 h-10 rounded-lg border-2 font-bold text-sm transition-all ${teacherObs[q.id] === lvl ? 'bg-amber-500 text-white border-amber-500' : 'border-gray-200 text-gray-600 hover:border-amber-300'}`} data-testid={`scr-obs-${lvl}`}>
                    {lvl}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between pt-2">
            <Button onClick={() => setCurrentQ(p => Math.max(0, p - 1))} variant="outline" size="sm" disabled={currentQ === 0}>Previous</Button>
            {isLast ? (
              <Button onClick={submitScreener} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white" data-testid="scr-submit-btn">
                {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle size={14} className="mr-1" />} Submit Screener
              </Button>
            ) : (
              <Button onClick={() => setCurrentQ(p => Math.min(questions.length - 1, p + 1))} className="bg-[#002FA7] text-white" data-testid="scr-next-btn">
                Next <ArrowRight size={14} className="ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── RESULTS VIEW ──
  if (view === 'results' && result) {
    const levelColors = { 1: 'text-red-600', 2: 'text-orange-600', 3: 'text-yellow-600', 4: 'text-blue-600', 5: 'text-indigo-600', 6: 'text-green-600' };

    return (
      <div className="space-y-5" data-testid="scr-results">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Screener Results</h2>
          <Button onClick={() => { setView('start'); setResult(null); setScreener(null); }} variant="outline" size="sm">New Screener</Button>
        </div>

        {/* Summary card */}
        <div className="bg-white border-2 border-[#002FA7] rounded-lg p-6 text-center">
          <p className="text-sm text-gray-500 mb-1">{result.student_name} | {result.grade_level} | Home Language: {result.home_language || 'Not specified'}</p>
          <div className={`text-5xl font-bold ${levelColors[result.estimated_wida_level] || 'text-gray-800'}`}>Level {result.estimated_wida_level}</div>
          <p className="text-lg font-medium text-gray-700 mt-1">{result.estimated_wida_name}</p>
          <p className="text-xs text-gray-400 mt-2">Composite: {result.estimated_composite}</p>
        </div>

        {/* Modality breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(result.modality_estimates || {}).map(([mod, score]) => (
            <div key={mod} className="bg-white border border-gray-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-800">{score}</div>
              <div className="text-[10px] text-gray-500 uppercase font-bold capitalize">{mod}</div>
            </div>
          ))}
        </div>

        {/* Recommendation */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-xs font-bold text-blue-700 mb-1">Placement Recommendation</p>
          <p className="text-sm text-blue-900">{result.recommendation}</p>
        </div>
      </div>
    );
  }

  // ── HISTORY VIEW ──
  if (view === 'history') {
    return (
      <div className="space-y-5" data-testid="scr-history">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Screener History</h2>
          <Button onClick={() => setView('start')} variant="outline" size="sm">New Screener</Button>
        </div>
        {loadingHistory ? (
          <div className="py-12 text-center"><Loader2 className="animate-spin mx-auto text-green-600" size={24} /></div>
        ) : history.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
            <UserSearch size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500 text-sm">No screener records yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {history.map(s => (
              <div key={s.screener_id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between group" data-testid={`scr-record-${s.screener_id}`}>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">{s.student_name}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{s.grade_level}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${s.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{s.status}</span>
                    {s.result && <span className="text-[10px] text-gray-500">Level {s.result.estimated_wida_level} ({s.result.estimated_wida_name})</span>}
                    <span className="text-[10px] text-gray-400">{formatDate(s.created_at)}</span>
                  </div>
                </div>
                <button onClick={() => deleteScreener(s.screener_id)} className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`scr-delete-${s.screener_id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
};

// ════════════════════════════════════════════════════════════
// SPEAKING PRACTICE LAB
// ════════════════════════════════════════════════════════════
const SpeakingLabTool = () => {
  const [view, setView] = useState('setup');
  const [form, setForm] = useState({ grade_level: '', wida_level: '' });
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [history, setHistory] = useState([]);

  const getPrompt = async () => {
    if (!form.grade_level) { toast.error('Select a grade band'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/teacher-toolkit/speaking-lab/get-prompt`, form, authHeader());
      setPrompt(res.data);
      setView('practice');
      setTranscript('');
      setFeedback(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'No prompts found'); }
    finally { setLoading(false); }
  };

  const submitResponse = async () => {
    if (!transcript.trim()) { toast.error('Enter the student\'s spoken response'); return; }
    setLoading(true);
    toast.info('the automated system is analyzing the response...');
    try {
      const res = await axios.post(`${API}/teacher-toolkit/speaking-lab/evaluate`, {
        prompt_id: prompt?.prompt_id || '',
        transcript,
        grade_level: form.grade_level,
        target_wida_level: parseInt(form.wida_level) || 3,
      }, authHeader());
      setFeedback(res.data.feedback);
      setView('feedback');
    } catch (err) { toast.error(err.response?.data?.detail || 'Evaluation failed'); }
    finally { setLoading(false); }
  };

  const scoreColor = (s) => s >= 5 ? 'text-green-600' : s >= 3 ? 'text-blue-600' : 'text-amber-600';

  if (view === 'setup') {
    return (
      <div className="space-y-5" data-testid="speaking-lab-setup">
        <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Speaking Practice Lab</h2>
        <p className="text-xs text-gray-500">Select grade and level, then transcribe or type the student's spoken response for automated feedback.</p>
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Grade Band *</label>
              <select value={form.grade_level} onChange={e => setForm(p => ({ ...p, grade_level: e.target.value }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="spk-grade">
                <option value="">Select...</option>
                {GRADE_BANDS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Target Proficiency Level</label>
              <select value={form.wida_level} onChange={e => setForm(p => ({ ...p, wida_level: e.target.value }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="spk-level">
                <option value="">Any</option>
                {WIDA_LEVELS.map(l => <option key={l.value} value={l.value}>Level {l.value}</option>)}
              </select>
            </div>
          </div>
          <Button onClick={getPrompt} disabled={loading} className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white text-base font-bold" data-testid="spk-get-prompt-btn">
            {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : <Mic size={18} className="mr-2" />} Get Speaking Prompt
          </Button>
        </div>
      </div>
    );
  }

  if (view === 'practice' && prompt) {
    return (
      <div className="space-y-4" data-testid="speaking-lab-practice">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Speaking Prompt</h2>
          <Button onClick={() => setView('setup')} variant="outline" size="sm">New Prompt</Button>
        </div>
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-5">
          {prompt.graphic_support_url && <img src={prompt.graphic_support_url} alt="Support" className="max-h-40 rounded-lg border mb-3" />}
          {prompt.bp_image_url && <img src={prompt.bp_image_url.startsWith('http') ? prompt.bp_image_url : `${process.env.REACT_APP_BACKEND_URL}${prompt.bp_image_url}`} alt="Big Picture" className="max-h-40 rounded-lg border mb-3" />}
          <p className="text-base font-medium text-gray-800">{prompt.question_text}</p>
          {prompt.stimulus && <p className="text-sm text-gray-500 mt-2 italic">{prompt.stimulus}</p>}
          <span className="text-[10px] bg-sky-100 text-sky-700 px-2 py-0.5 rounded mt-2 inline-block">Proficiency Level {prompt.wida_level}</span>
        </div>
        <div>
          <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Student's Response (type or transcribe what they said)</label>
          <textarea value={transcript} onChange={e => setTranscript(e.target.value)} placeholder="Type the student's spoken response here..." className="w-full min-h-[120px] text-sm border border-gray-300 rounded-lg p-3" data-testid="spk-transcript" />
        </div>
        <Button onClick={submitResponse} disabled={loading} className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white font-bold" data-testid="spk-evaluate-btn">
          {loading ? <><Loader2 size={18} className="animate-spin mr-2" /> Evaluating...</> : <><Sparkles size={18} className="mr-2" /> Get MOSAIC Feedback</>}
        </Button>
      </div>
    );
  }

  if (view === 'feedback' && feedback) {
    return (
      <div className="space-y-4" data-testid="speaking-lab-feedback">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Speaking Feedback</h2>
          <div className="flex gap-2">
            <Button onClick={() => { setView('practice'); setFeedback(null); setTranscript(''); }} variant="outline" size="sm" className="gap-1"><RotateCcw size={14} /> Try Again</Button>
            <Button onClick={() => setView('setup')} variant="outline" size="sm">New Prompt</Button>
          </div>
        </div>
        <div className="bg-white border-2 border-sky-200 rounded-lg p-5 text-center">
          <div className={`text-4xl font-bold ${scoreColor(feedback.overall_level)}`}>Level {feedback.overall_level}</div>
          <p className="text-xs text-gray-500 mt-1">Overall MOSAIC Speaking Level</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[{k: 'linguistic_complexity', label: 'Linguistic Complexity'}, {k: 'vocabulary_usage', label: 'Vocabulary'}, {k: 'language_control', label: 'Language Control'}].map(dim => (
            <div key={dim.k} className="bg-white border border-gray-200 rounded-lg p-3">
              <div className={`text-2xl font-bold ${scoreColor(feedback[dim.k]?.score)}`}>{feedback[dim.k]?.score || '?'}</div>
              <div className="text-[10px] font-bold text-gray-500 uppercase">{dim.label}</div>
              <p className="text-xs text-gray-600 mt-1">{feedback[dim.k]?.feedback}</p>
            </div>
          ))}
        </div>
        {feedback.strengths && <div className="bg-green-50 border border-green-200 rounded-lg p-3"><p className="text-xs font-bold text-green-700 mb-1">Strengths</p>{feedback.strengths.map((s, i) => <p key={i} className="text-xs text-green-800">+ {s}</p>)}</div>}
        {feedback.areas_to_improve && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3"><p className="text-xs font-bold text-amber-700 mb-1">Areas to Improve</p>{feedback.areas_to_improve.map((a, i) => <p key={i} className="text-xs text-amber-800">- {a}</p>)}</div>}
        {feedback.model_response && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3"><p className="text-xs font-bold text-blue-700 mb-1">Model Response (Target Level)</p><p className="text-xs text-blue-800 italic">"{feedback.model_response}"</p></div>}
        {feedback.encouragement && <p className="text-sm text-center text-gray-600 italic">{feedback.encouragement}</p>}
      </div>
    );
  }
  return null;
};

// ════════════════════════════════════════════════════════════
// VOCABULARY BUILDER
// ════════════════════════════════════════════════════════════
const VOCAB_CATEGORIES = ['school', 'classroom', 'family', 'community', 'science', 'math', 'social_studies', 'arts', 'health', 'technology'];

const VocabBuilderTool = () => {
  const [view, setView] = useState('setup');
  const [form, setForm] = useState({ grade_level: '3-4', wida_level: 3, category: 'school', word_count: 8 });
  const [loading, setLoading] = useState(false);
  const [vocabSet, setVocabSet] = useState(null);
  const [currentWord, setCurrentWord] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState(null);

  const generateSet = async () => {
    setLoading(true);
    toast.info('Generating vocabulary set...');
    try {
      const res = await axios.post(`${API}/teacher-toolkit/vocab-builder/generate-set`, form, authHeader());
      setVocabSet(res.data);
      setCurrentWord(0);
      setAnswers({});
      setResults(null);
      setView('practice');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate'); }
    finally { setLoading(false); }
  };

  const submitAnswers = async () => {
    if (!vocabSet) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/teacher-toolkit/vocab-builder/submit-practice`, { set_id: vocabSet.set_id, answers }, authHeader());
      setResults(res.data);
      setView('results');
    } catch (err) { toast.error('Failed to submit'); }
    finally { setLoading(false); }
  };

  const loadProgress = async () => {
    try {
      const res = await axios.get(`${API}/teacher-toolkit/vocab-builder/progress`, authHeader());
      setProgress(res.data);
      setView('progress');
    } catch { }
  };

  if (view === 'setup') {
    return (
      <div className="space-y-5" data-testid="vocab-builder-setup">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Vocabulary Builder</h2>
            <p className="text-xs text-gray-500">MOSAIC-generated proficiency-level vocabulary with definitions, sentences, and practice questions</p>
          </div>
          <Button onClick={loadProgress} variant="outline" size="sm" className="gap-1"><TrendingUp size={14} /> Progress</Button>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Grade Band</label>
              <select value={form.grade_level} onChange={e => setForm(p => ({ ...p, grade_level: e.target.value }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="vb-grade">
                {GRADE_BANDS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Proficiency Level</label>
              <select value={form.wida_level} onChange={e => setForm(p => ({ ...p, wida_level: parseInt(e.target.value) }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="vb-level">
                {WIDA_LEVELS.map(l => <option key={l.value} value={l.value}>Level {l.value}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Category</label>
            <div className="flex flex-wrap gap-2">
              {VOCAB_CATEGORIES.map(cat => (
                <button key={cat} onClick={() => setForm(p => ({ ...p, category: cat }))} className={`px-3 py-1.5 text-xs font-bold rounded-full border-2 transition-all capitalize ${form.category === cat ? 'bg-purple-500 text-white border-transparent' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`} data-testid={`vb-cat-${cat}`}>
                  {cat.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
          <Button onClick={generateSet} disabled={loading} className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white text-base font-bold" data-testid="vb-generate-btn">
            {loading ? <><Loader2 size={18} className="animate-spin mr-2" /> Generating...</> : <><BookText size={18} className="mr-2" /> Generate Vocabulary Set</>}
          </Button>
        </div>
      </div>
    );
  }

  if (view === 'practice' && vocabSet) {
    const words = vocabSet.words || [];
    const word = words[currentWord];
    if (!word) return null;
    return (
      <div className="space-y-4" data-testid="vocab-builder-practice">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-gray-700">Word {currentWord + 1} of {words.length}</span>
          <Button onClick={() => setView('setup')} variant="outline" size="sm">Exit</Button>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5"><div className="bg-purple-500 h-1.5 rounded-full transition-all" style={{ width: `${(currentWord + 1) / words.length * 100}%` }} /></div>

        {/* Word Card */}
        <div className="bg-white border-2 border-purple-200 rounded-lg p-5">
          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-2xl font-bold text-purple-700">{word.word}</span>
            <span className="text-xs text-gray-400 italic">{word.part_of_speech}</span>
            {word.tier && <span className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">Tier {word.tier}</span>}
          </div>
          <p className="text-sm text-gray-700 mb-2">{word.definition}</p>
          <p className="text-sm text-gray-500 italic">"{word.example_sentence}"</p>
          {word.spanish_translation && <p className="text-xs text-amber-600 mt-1">Spanish: {word.spanish_translation}</p>}
          {word.word_family && <p className="text-[10px] text-gray-400 mt-1">Word family: {word.word_family.join(', ')}</p>}
        </div>

        {/* Practice Questions */}
        <div className="space-y-3">
          {(word.practice_questions || []).map((pq, qi) => (
            <div key={qi} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`vb-pq-${qi}`}>
              <p className="text-sm font-medium text-gray-800 mb-2">{pq.question || pq.statement}</p>
              {pq.type === 'multiple_choice' && (
                <div className="space-y-1.5">
                  {(pq.options || []).map((opt, oi) => {
                    const sel = answers[word.word]?.[String(qi)] === opt;
                    return (
                      <button key={oi} onClick={() => setAnswers(prev => ({ ...prev, [word.word]: { ...prev[word.word], [String(qi)]: opt } }))} className={`w-full text-left px-3 py-2 rounded border-2 text-sm transition-all ${sel ? 'border-purple-500 bg-purple-50 font-bold' : 'border-gray-200 hover:border-gray-400'}`}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}
              {pq.type === 'fill_blank' && (
                <Input value={answers[word.word]?.[String(qi)] || ''} onChange={e => setAnswers(prev => ({ ...prev, [word.word]: { ...prev[word.word], [String(qi)]: e.target.value } }))} placeholder="Type your answer..." className="h-9 border-gray-300" />
              )}
              {pq.type === 'true_false' && (
                <div className="flex gap-2">
                  {['True', 'False'].map(tf => {
                    const sel = answers[word.word]?.[String(qi)] === tf.toLowerCase();
                    return <button key={tf} onClick={() => setAnswers(prev => ({ ...prev, [word.word]: { ...prev[word.word], [String(qi)]: tf.toLowerCase() } }))} className={`px-4 py-2 rounded border-2 text-sm font-bold transition-all ${sel ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-400'}`}>{tf}</button>;
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-between">
          <Button onClick={() => setCurrentWord(p => Math.max(0, p - 1))} variant="outline" size="sm" disabled={currentWord === 0}>Previous</Button>
          {currentWord === words.length - 1 ? (
            <Button onClick={submitAnswers} disabled={loading} className="bg-purple-600 text-white" data-testid="vb-submit-btn">
              {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle size={14} className="mr-1" />} Submit All
            </Button>
          ) : (
            <Button onClick={() => setCurrentWord(p => p + 1)} className="bg-[#002FA7] text-white">Next <ArrowRight size={14} className="ml-1" /></Button>
          )}
        </div>
      </div>
    );
  }

  if (view === 'results' && results) {
    return (
      <div className="space-y-4" data-testid="vocab-builder-results">
        <h2 className="text-lg font-bold text-gray-800">Results</h2>
        <div className="bg-white border-2 border-purple-200 rounded-lg p-6 text-center">
          <div className={`text-4xl font-bold ${results.score_pct >= 70 ? 'text-green-600' : results.score_pct >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{results.score_pct}%</div>
          <p className="text-sm text-gray-500">{results.correct} / {results.total} correct</p>
        </div>
        <div className="flex gap-2 justify-center">
          <Button onClick={() => { setView('setup'); setResults(null); }} variant="outline" className="gap-1"><RotateCcw size={14} /> New Set</Button>
          <Button onClick={loadProgress} variant="outline" className="gap-1"><TrendingUp size={14} /> View Progress</Button>
        </div>
      </div>
    );
  }

  if (view === 'progress' && progress) {
    return (
      <div className="space-y-4" data-testid="vocab-builder-progress">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Vocabulary Progress</h2>
          <Button onClick={() => setView('setup')} variant="outline" size="sm">New Set</Button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{progress.stats?.total_sessions || 0}</div>
            <div className="text-[10px] text-gray-500 uppercase font-bold">Sessions</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{progress.stats?.average_score || 0}%</div>
            <div className="text-[10px] text-gray-500 uppercase font-bold">Avg Score</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{progress.stats?.total_words_practiced || 0}</div>
            <div className="text-[10px] text-gray-500 uppercase font-bold">Words Practiced</div>
          </div>
        </div>
        {(progress.records || []).length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b"><span className="text-xs font-bold text-gray-700">Recent Sessions</span></div>
            {progress.records.map((r, i) => (
              <div key={i} className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between text-sm">
                <div>
                  <span className="text-xs bg-gray-100 text-gray-600 px-1.5 rounded capitalize">{r.category?.replace('_',' ')}</span>
                  <span className="text-xs text-gray-400 ml-2">{r.grade_level} L{r.wida_level}</span>
                </div>
                <span className={`font-bold ${r.score_pct >= 70 ? 'text-green-600' : 'text-amber-600'}`}>{r.score_pct}%</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  return null;
};

// ════════════════════════════════════════════════════════════
// WRITING PORTFOLIO
// ════════════════════════════════════════════════════════════
const WritingPortfolioTool = () => {
  const [view, setView] = useState('list');
  const [form, setForm] = useState({ grade_level: '', wida_level: '' });
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [writingText, setWritingText] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/teacher-toolkit/writing-portfolio/entries`, authHeader());
      setEntries(res.data.entries || []);
    } catch { }
  };

  const getPrompt = async () => {
    if (!form.grade_level) { toast.error('Select a grade band'); return; }
    try {
      const res = await axios.post(`${API}/teacher-toolkit/writing-portfolio/get-prompt`, form, authHeader());
      setPrompt(res.data);
      setWritingText('');
      setFeedback(null);
      setView('write');
    } catch (err) { toast.error(err.response?.data?.detail || 'No prompts found'); }
  };

  const submitWriting = async (isRevision = false, originalId = '') => {
    if (!writingText.trim() || writingText.trim().length < 10) { toast.error('Write at least a few sentences'); return; }
    setLoading(true);
    toast.info('the automated system is reviewing your writing...');
    try {
      const res = await axios.post(`${API}/teacher-toolkit/writing-portfolio/submit`, {
        prompt_id: prompt?.prompt_id || '',
        writing_text: writingText,
        grade_level: form.grade_level,
        target_wida_level: parseInt(form.wida_level) || 3,
        is_revision: isRevision,
        original_entry_id: originalId,
      }, authHeader());
      setFeedback(res.data.feedback);
      setView('feedback');
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || 'Evaluation failed'); }
    finally { setLoading(false); }
  };

  const openEntry = async (entryId) => {
    try {
      const res = await axios.get(`${API}/teacher-toolkit/writing-portfolio/${entryId}`, authHeader());
      setSelectedEntry(res.data);
      setView('detail');
    } catch { toast.error('Failed to load entry'); }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Delete this writing entry?')) return;
    try {
      await axios.delete(`${API}/teacher-toolkit/writing-portfolio/${entryId}`, authHeader());
      toast.success('Deleted');
      fetchEntries();
    } catch { toast.error('Failed to delete'); }
  };

  const scoreColor = (s) => s >= 5 ? 'text-green-600' : s >= 3 ? 'text-blue-600' : 'text-amber-600';
  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  if (view === 'list') {
    return (
      <div className="space-y-5" data-testid="writing-portfolio-list">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Writing Portfolio</h2>
            <p className="text-xs text-gray-500">automated writing practice with MOSAIC proficiency feedback. Submit, revise, and track growth.</p>
          </div>
          <Button onClick={() => setView('setup')} className="bg-rose-600 hover:bg-rose-700 text-white gap-1" data-testid="wp-new-btn"><PenLine size={14} /> New Entry</Button>
        </div>
        {entries.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
            <PenLine size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No writing entries yet</p>
            <Button onClick={() => setView('setup')} variant="outline" className="mt-3 gap-1"><PenLine size={14} /> Start Writing</Button>
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map(e => (
              <div key={e.entry_id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-rose-300 transition-all cursor-pointer group" onClick={() => openEntry(e.entry_id)} data-testid={`wp-entry-${e.entry_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-800 text-sm truncate">{e.prompt_text || 'Free Writing'}</h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{e.grade_level}</span>
                      {e.feedback?.overall_level && <span className={`text-[10px] font-bold ${scoreColor(e.feedback.overall_level)}`}>Level {e.feedback.overall_level}</span>}
                      {e.is_revision && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Revision</span>}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">{formatDate(e.created_at)}</p>
                  </div>
                  <button onClick={(ev) => { ev.stopPropagation(); deleteEntry(e.entry_id); }} className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (view === 'setup') {
    return (
      <div className="space-y-5" data-testid="writing-portfolio-setup">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">New Writing Entry</h2>
          <Button onClick={() => setView('list')} variant="outline" size="sm">Back</Button>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Grade Band *</label>
              <select value={form.grade_level} onChange={e => setForm(p => ({ ...p, grade_level: e.target.value }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="wp-grade">
                <option value="">Select...</option>
                {GRADE_BANDS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Target Proficiency Level</label>
              <select value={form.wida_level} onChange={e => setForm(p => ({ ...p, wida_level: e.target.value }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="wp-level">
                <option value="">Any</option>
                {WIDA_LEVELS.map(l => <option key={l.value} value={l.value}>Level {l.value}</option>)}
              </select>
            </div>
          </div>
          <Button onClick={getPrompt} className="w-full h-12 bg-rose-600 hover:bg-rose-700 text-white text-base font-bold" data-testid="wp-get-prompt-btn">
            <Sparkles size={18} className="mr-2" /> Get Writing Prompt
          </Button>
        </div>
      </div>
    );
  }

  if (view === 'write' && prompt) {
    return (
      <div className="space-y-4" data-testid="writing-portfolio-write">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Write</h2>
          <Button onClick={() => setView('list')} variant="outline" size="sm">Cancel</Button>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
          {prompt.graphic_support_url && <img src={prompt.graphic_support_url} alt="Support" className="max-h-36 rounded-lg border mb-3" />}
          <p className="text-base font-medium text-gray-800">{prompt.question_text}</p>
          <span className="text-[10px] bg-rose-100 text-rose-700 px-2 py-0.5 rounded mt-2 inline-block">Proficiency Level {prompt.wida_level}</span>
        </div>
        <div>
          <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Your Writing</label>
          <textarea value={writingText} onChange={e => setWritingText(e.target.value)} placeholder="Start writing here..." className="w-full min-h-[200px] text-sm border border-gray-300 rounded-lg p-3" data-testid="wp-text" />
          <p className="text-[10px] text-gray-400 text-right mt-1">{writingText.split(/\s+/).filter(Boolean).length} words</p>
        </div>
        <Button onClick={() => submitWriting(false)} disabled={loading} className="w-full h-12 bg-rose-600 hover:bg-rose-700 text-white font-bold" data-testid="wp-submit-btn">
          {loading ? <><Loader2 size={18} className="animate-spin mr-2" /> Evaluating...</> : <><Sparkles size={18} className="mr-2" /> Submit for MOSAIC Feedback</>}
        </Button>
      </div>
    );
  }

  if (view === 'feedback' && feedback) {
    return (
      <div className="space-y-4" data-testid="writing-portfolio-feedback">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Writing Feedback</h2>
          <Button onClick={() => setView('list')} variant="outline" size="sm">Portfolio</Button>
        </div>
        <div className="bg-white border-2 border-rose-200 rounded-lg p-5 text-center">
          <div className={`text-4xl font-bold ${scoreColor(feedback.overall_level)}`}>Level {feedback.overall_level}</div>
          <p className="text-xs text-gray-500 mt-1">Overall MOSAIC Writing Level</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[{k: 'linguistic_complexity', label: 'Linguistic Complexity'}, {k: 'vocabulary_usage', label: 'Vocabulary'}, {k: 'language_control', label: 'Language Control'}].map(dim => (
            <div key={dim.k} className="bg-white border border-gray-200 rounded-lg p-3">
              <div className={`text-2xl font-bold ${scoreColor(feedback[dim.k]?.score)}`}>{feedback[dim.k]?.score || '?'}</div>
              <div className="text-[10px] font-bold text-gray-500 uppercase">{dim.label}</div>
              <p className="text-xs text-gray-600 mt-1">{feedback[dim.k]?.feedback}</p>
            </div>
          ))}
        </div>
        {feedback.strengths && <div className="bg-green-50 border border-green-200 rounded-lg p-3"><p className="text-xs font-bold text-green-700 mb-1">Strengths</p>{feedback.strengths.map((s, i) => <p key={i} className="text-xs text-green-800">+ {s}</p>)}</div>}
        {feedback.revision_suggestions && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3"><p className="text-xs font-bold text-amber-700 mb-1">Revision Suggestions</p>{feedback.revision_suggestions.map((s, i) => <p key={i} className="text-xs text-amber-800">- {s}</p>)}</div>}
        {feedback.encouragement && <p className="text-sm text-center text-gray-600 italic">{feedback.encouragement}</p>}
      </div>
    );
  }

  if (view === 'detail' && selectedEntry) {
    const e = selectedEntry.entry || {};
    const fb = e.feedback || {};
    return (
      <div className="space-y-4" data-testid="writing-portfolio-detail">
        <div className="flex items-center justify-between">
          <Button onClick={() => { setView('list'); setSelectedEntry(null); }} variant="outline" size="sm">Back</Button>
          <Button onClick={() => { setPrompt({ prompt_id: e.prompt_id, question_text: e.prompt_text, wida_level: e.target_wida_level }); setWritingText(e.writing_text || ''); setView('write'); }} variant="outline" size="sm" className="gap-1"><RotateCcw size={14} /> Revise</Button>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-bold text-gray-500 mb-1">Prompt</p>
          <p className="text-sm text-gray-800">{e.prompt_text}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-bold text-gray-500 mb-1">Writing ({fb.word_count || '?'} words)</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{e.writing_text}</p>
        </div>
        {fb.overall_level && (
          <div className="grid grid-cols-3 gap-3">
            {[{k: 'linguistic_complexity', label: 'Complexity'}, {k: 'vocabulary_usage', label: 'Vocabulary'}, {k: 'language_control', label: 'Control'}].map(dim => (
              <div key={dim.k} className="bg-white border border-gray-200 rounded-lg p-3 text-center">
                <div className={`text-xl font-bold ${scoreColor(fb[dim.k]?.score)}`}>{fb[dim.k]?.score || '?'}</div>
                <div className="text-[10px] text-gray-500 uppercase">{dim.label}</div>
              </div>
            ))}
          </div>
        )}
        {(selectedEntry.revisions || []).length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-bold text-gray-700 mb-1">Revision History</p>
            {selectedEntry.revisions.map((r, i) => (
              <div key={i} className="text-xs text-gray-600 flex items-center gap-2 py-1">
                <span className="font-bold">Rev {r.revision_number}</span>
                <span>Level {r.feedback?.overall_level || '?'}</span>
                <span className="text-gray-400">{formatDate(r.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default TeacherToolkitTab;
